"use client";

import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { joinPhones, normalizePhone, phoneTelHref, splitPhones } from "@/lib/phone";
import { ContactRoundIcon, PlusIcon } from "lucide-react";
import { SmoothReveal } from "@/components/smooth-reveal";

export function PhoneChips({
  value,
  onRemove,
}: {
  value: string;
  onRemove?: (number: string) => void;
}) {
  const phones = splitPhones(value);
  if (phones.length === 0) return null;
  return (
    <>
      {phones.map((number) =>
        onRemove ? (
          <span
            key={number}
            className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-accent py-0.5 pl-2 pr-0.5 text-xs"
          >
            {number}
            <button
              type="button"
              className="flex size-5 items-center justify-center rounded-full text-muted-foreground"
              aria-label={`Прибрати ${number}`}
              onClick={() => onRemove(number)}
            >
              ×
            </button>
          </span>
        ) : (
          <a
            key={number}
            href={phoneTelHref(number)}
            className="shrink-0 rounded-full bg-accent px-2.5 py-1 text-xs text-primary"
          >
            {number}
          </a>
        ),
      )}
    </>
  );
}

export function PhoneEditor({
  label,
  value,
  onCommit,
  onPick,
  showContact = false,
}: {
  label?: string;
  hint?: string;
  value: string;
  onCommit: (next: string) => void | Promise<void>;
  onPick?: (focus: () => void) => void;
  showContact?: boolean;
  compact?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const phones = splitPhones(value);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  async function addDraft() {
    const incoming = splitPhones(draft);
    if (incoming.length === 0) {
      if (draft.replace(/\D/g, "").length > 0) {
        toast.error("Схоже, це не повний номер.");
      }
      return false;
    }
    const next = joinPhones([...phones, ...incoming]);
    setDraft("");
    setOpen(false);
    if (next !== normalizePhone(value)) {
      await onCommit(next);
    }
    return true;
  }

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {label ? <span className="text-sm font-medium">{label}</span> : null}
      <PhoneChips
        value={value}
        onRemove={(number) => void onCommit(joinPhones(phones.filter((item) => item !== number)))}
      />
      <SmoothReveal open={open} className="basis-full">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-1">
          <Input
            ref={inputRef}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void addDraft();
              }
              if (event.key === "Escape") {
                setDraft("");
                setOpen(false);
              }
            }}
            onBlur={() => {
              if (splitPhones(draft).length > 0) {
                void addDraft();
                return;
              }
              if (!draft.trim()) setOpen(false);
            }}
            placeholder="097 000 00 00"
            className="h-8 w-36 min-w-0 rounded-xl text-xs"
            aria-label={label ?? "Телефон"}
          />
          <Button
            type="button"
            variant="outline"
            className="h-8 shrink-0 rounded-xl px-2.5"
            disabled={!draft.trim()}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => void addDraft()}
            aria-label="Додати номер"
          >
            <PlusIcon className="size-4" />
          </Button>
          {showContact && onPick ? (
            <button
              type="button"
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-xl border border-border bg-background"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onPick(() => inputRef.current?.focus())}
              aria-label="З книги"
            >
              <ContactRoundIcon className="size-4" />
            </button>
          ) : null}
        </div>
      </SmoothReveal>
      {open ? null : (
        <button
          type="button"
          className="shrink-0 text-xs font-medium text-primary"
          onClick={() => setOpen(true)}
        >
          Додати номер
        </button>
      )}
    </div>
  );
}
